import { CheckOutlined } from '@ant-design/icons';
import { message, Space, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { FileInfo } from '../../../../../../generated';
import FilePreview from '../../../../../shared/file-preview/file-preview';
import { AuthResult, AzureBlobResponse } from '../../../../../shared/file-upload/azure-upload';
import { FileUploadButton } from '../../../../../shared/file-upload/file-upload-button';
import { GenerateVerificationFormButtonContainer } from './generate-verification-form-button.container';

interface FileUploadWithGenerateVerificationFormButtonProps {
  title?: string;
  caseId: string;
  description?: string; // text below the title (optional)
  buttonText?: string; // text on the upload button
  permittedContentTypes?: string[];
  permittedExtensions?: string[];
  onAuthorizeRequest: (file: File) => Promise<AuthResult>; // called before uploading or when previewing a file
  fetchFilePreviewData: () => Promise<FileInfo>;
  onUploadSuccess: (uploadedData: AzureBlobResponse) => Promise<void>;
  isPreviewOnly?: boolean;
  setIsEditing?: React.Dispatch<React.SetStateAction<boolean>>;
  isShowFileName?: boolean;
  isUploadingPreviewImage?: boolean;
}

export const FileUploadWithGenerateVerificationFormButton: React.FC<FileUploadWithGenerateVerificationFormButtonProps> = ({ isPreviewOnly = false, ...props }) => {
  const [previewFileData, setPreviewFileData] = useState<FileInfo | null>(null);
  const [isProcessingUploadingFile, setIsProcessingUploadingFile] = useState(false);
  const [isProcessingGeneratingPdf, setIsProcessingGeneratingPdf] = useState(false);
  const [hasNewFileUploaded, setHasNewFileUploaded] = useState(false);

  useEffect(() => {
    const doGetFilePreviewData = async () => {
      setIsProcessingUploadingFile(true);
      await getFilePreviewData();
      setIsProcessingUploadingFile(false);
    };
    doGetFilePreviewData();
  }, []);

  const getFilePreviewData = async () => {
    const fileData = await props.fetchFilePreviewData();
    setPreviewFileData(fileData);
  };

  const handleAuthorizeRequestForUploadFile = async (file: File): Promise<AuthResult> => {
    setIsProcessingUploadingFile(true);
    setHasNewFileUploaded(false);
    return props.onAuthorizeRequest(file);
  };

  const handleAuthorizeRequestForUploadGeneratedPdf = async (file: File): Promise<AuthResult> => {
    setHasNewFileUploaded(false);
    return props.onAuthorizeRequest(file);
  };

  const handleOnError = () => {
    setIsProcessingUploadingFile(false);
    setIsProcessingGeneratingPdf(false);
  };

  const handleOnUploadSuccess = async (response: AzureBlobResponse) => {
    await doLastStepAfterUploadOrGeneratePdfSuccess(response);
    message.success('File uploaded successfully');
  };

  const handleOnGeneratePdfSuccess = async (response: AzureBlobResponse) => {
    await doLastStepAfterUploadOrGeneratePdfSuccess(response);
  };

  const doLastStepAfterUploadOrGeneratePdfSuccess = async (response: AzureBlobResponse) => {
    await getFilePreviewData();
    if (props.setIsEditing) {
      props.setIsEditing(!isPreviewOnly);
    }
    await props.onUploadSuccess(response);
    setHasNewFileUploaded(true);
    setIsProcessingUploadingFile(false);
    setIsProcessingGeneratingPdf(false);
  };

  const handleStartPdfGeneration = () => {
    setHasNewFileUploaded(false);
    setIsProcessingGeneratingPdf(true);
  };

  return (
    <div
      style={{
        fontSize: '10px',
        margin: '10px'
      }}
    >
      {props.title && <Typography.Title level={5}>{props.title}</Typography.Title>}
      {props.description && (
        <div
          style={{
            marginBottom: '10px'
          }}
        >
          {props.description}
        </div>
      )}
      <Space direction='vertical' size={2}>
        <Space direction="horizontal" style={{ alignItems: 'baseline' }}>
          <FilePreview
            fileUrl={previewFileData?.url ?? ''}
            fileName={previewFileData?.name ?? ''}
            fileType={previewFileData?.type ?? ''}
            isProcessing={isProcessingUploadingFile || isProcessingGeneratingPdf}
            isShowFileName={props.isShowFileName}
          />
          {hasNewFileUploaded && <CheckOutlined style={{ color: '#52c41a', paddingLeft: '5px' }} />}
        </Space>
        {!isPreviewOnly && (
          <>
            <GenerateVerificationFormButtonContainer
              onStartPdfGeneration={handleStartPdfGeneration}
              isProcessing={isProcessingGeneratingPdf}
              isProcessingUploadingFile={isProcessingUploadingFile}
              authorizeRequest={handleAuthorizeRequestForUploadGeneratedPdf}
              onSuccess={handleOnGeneratePdfSuccess}
              caseId={props.caseId ?? ''}
              setIsEditing={props.setIsEditing}
            />
            <FileUploadButton
              isUploadPreviewImage={props.isUploadingPreviewImage}
              isProcessing={isProcessingUploadingFile}
              isProcessingGeneratingPdf={isProcessingGeneratingPdf}
              authorizeRequest={handleAuthorizeRequestForUploadFile}
              buttonText={props.buttonText}
              maxFileCount={1}
              permittedContentTypes={props.permittedContentTypes}
              permittedExtensions={props.permittedExtensions}
              maxFileSizeBytes={10 * 1024 * 1024} // 10MB,
              maxWidthOrHeight={2048}
              onSuccess={handleOnUploadSuccess}
              onError={handleOnError}
              setIsEditing={props.setIsEditing}
            />
          </>
        )}
      </Space>
    </div>
  );
};
